/**
 * AI Chat Analytics API client
 *
 * Hits the chat-module admin analytics endpoints. Auth: admin JWT from
 * sessionStorage (same pattern as aiChatApi.js).
 */

function getChatBaseUrl() {
    const override = localStorage.getItem('chatBaseUrl');
    if (override) return override;
    const env = localStorage.getItem('env') === 'true';
    return env
        ? 'https://prod-chat-module-api.dugoutlive.com'
        : 'https://chat-module-api.dugoutlive.com';
}

function getAuthHeaders() {
    const token = sessionStorage.getItem('sessionToken');
    if (!token) return {};
    return { Authorization: `Bearer ${token}`, auth: token };
}

async function handleResponse(res) {
    if (!res.ok) {
        let errorMsg = `Request failed: ${res.status}`;
        try {
            const body = await res.json();
            errorMsg = body.message || body.error || errorMsg;
        } catch {}
        throw new Error(errorMsg);
    }
    return res.json();
}

/**
 * Build a query string from a filter object, dropping empty/null values so
 * the backend gets only what the dashboard actively cares about.
 */
function qs(filters = {}) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
        if (v == null || v === '' || v === false) continue;
        params.append(k, String(v));
    }
    const s = params.toString();
    return s ? `?${s}` : '';
}

export async function getAnalyticsOverview(filters) {
    const res = await fetch(`${getChatBaseUrl()}/admin/ai-chat/analytics/overview${qs(filters)}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
}

export async function getAnalyticsTimeseries(filters) {
    const res = await fetch(`${getChatBaseUrl()}/admin/ai-chat/analytics/timeseries${qs(filters)}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
}

export async function getAnalyticsIntents(filters) {
    const res = await fetch(`${getChatBaseUrl()}/admin/ai-chat/analytics/intents${qs(filters)}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
}

export async function getAnalyticsTopMatches(filters) {
    const res = await fetch(`${getChatBaseUrl()}/admin/ai-chat/analytics/top-matches${qs(filters)}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
}

export async function getAnalyticsTopUsers(filters) {
    const res = await fetch(`${getChatBaseUrl()}/admin/ai-chat/analytics/top-users${qs(filters)}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
}

export async function getAnalyticsMessages(filters) {
    const res = await fetch(`${getChatBaseUrl()}/admin/ai-chat/analytics/messages${qs(filters)}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
}

export async function getAnalyticsConversation(sessionId) {
    const res = await fetch(`${getChatBaseUrl()}/admin/ai-chat/analytics/conversation/${encodeURIComponent(sessionId)}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
}

export async function getAnalyticsModels(filters) {
    const res = await fetch(`${getChatBaseUrl()}/admin/ai-chat/analytics/models${qs(filters)}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
}

export async function getAnalyticsIntentModelMatrix(filters) {
    const res = await fetch(`${getChatBaseUrl()}/admin/ai-chat/analytics/intent-model-matrix${qs(filters)}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
}

export async function getAnalyticsRoutingConfig() {
    const res = await fetch(`${getChatBaseUrl()}/admin/ai-chat/analytics/routing`, {
        credentials: 'include',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
}

export async function getAnalyticsMatchBreakdown(filters) {
    const res = await fetch(`${getChatBaseUrl()}/admin/ai-chat/analytics/match-breakdown${qs(filters)}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
}

export async function getAnalyticsMatchUsers(matchId, filters) {
    const res = await fetch(`${getChatBaseUrl()}/admin/ai-chat/analytics/match/${encodeURIComponent(matchId)}/users${qs(filters)}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
}

export async function getAnalyticsMatchUserConversation(matchId, userId) {
    const res = await fetch(`${getChatBaseUrl()}/admin/ai-chat/analytics/match/${encodeURIComponent(matchId)}/user/${encodeURIComponent(userId)}/conversation`, {
        credentials: 'include',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
}
